import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Mail, Pencil, Eye, RotateCcw, Loader2, Code, FileText, 
  CheckCircle, XCircle, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminEmailTemplates = () => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [editForm, setEditForm] = useState({
    name: '',
    subject: '',
    html_content: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      name: template.name,
      subject: template.subject,
      html_content: template.html_content,
      description: template.description || '',
      is_active: template.is_active
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/email-templates/${selectedTemplate.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template saved successfully');
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (templateId) => {
    if (!window.confirm('Reset this template to its default content?')) return;
    
    try {
      await axios.post(
        `${API_URL}/api/email-templates/${templateId}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template reset to default');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const handlePreview = async (templateKey) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/email-templates/preview?template_key=${templateKey}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewContent(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await axios.put(
        `${API_URL}/api/email-templates/${template.id}`,
        { is_active: !template.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(template.is_active ? 'Template deactivated' : 'Template activated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-email-templates">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="h-6 w-6 text-maroon" />
            Email Templates
          </h1>
          <p className="text-gray-500">Manage email notification templates</p>
        </div>

        {/* Templates List */}
        <div className="grid gap-4">
          {templates.map(template => (
            <Card key={template.id} className={`${!template.is_active ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="h-4 w-4 text-maroon" />
                      {template.name}
                      {template.is_active ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {template.description}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handlePreview(template.template_key)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openEditDialog(template)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleReset(template.id)}
                      title="Reset to default"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Subject:</span>
                    <span className="ml-2 font-medium">{template.subject}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Key:</span>
                    <code className="ml-2 bg-gray-100 px-2 py-0.5 rounded text-xs">{template.template_key}</code>
                  </div>
                  {template.variables && template.variables.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-gray-500">Variables:</span>
                      {template.variables.slice(0, 5).map((v, i) => (
                        <code key={i} className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-xs">{v}</code>
                      ))}
                      {template.variables.length > 5 && (
                        <span className="text-gray-400 text-xs">+{template.variables.length - 5} more</span>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Email Template</DialogTitle>
              <DialogDescription>
                Customize the email template content. Use variables like {"{{first_name}}"} to personalize emails.
              </DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue="editor" className="mt-4">
              <TabsList>
                <TabsTrigger value="editor">
                  <Code className="h-4 w-4 mr-2" />
                  HTML Editor
                </TabsTrigger>
                <TabsTrigger value="preview">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="editor" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Template Name</Label>
                    <Input 
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Active</Label>
                      <p className="text-xs text-gray-500">Enable this template</p>
                    </div>
                    <Switch 
                      checked={editForm.is_active}
                      onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Email Subject</Label>
                  <Input 
                    value={editForm.subject}
                    onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                    placeholder="e.g., Welcome to {{event_name}}!"
                  />
                </div>
                
                <div>
                  <Label>Description</Label>
                  <Input 
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="When is this email sent?"
                  />
                </div>
                
                <div>
                  <Label>HTML Content</Label>
                  <Textarea 
                    value={editForm.html_content}
                    onChange={(e) => setEditForm({ ...editForm, html_content: e.target.value })}
                    rows={20}
                    className="font-mono text-sm"
                    placeholder="<div>Your HTML content here...</div>"
                  />
                </div>
                
                {selectedTemplate?.variables && selectedTemplate.variables.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-blue-900 mb-2">Available Variables:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.variables.map((v, i) => (
                        <code 
                          key={i} 
                          className="bg-white border border-blue-200 text-blue-700 px-2 py-1 rounded text-xs cursor-pointer hover:bg-blue-100"
                          onClick={() => {
                            navigator.clipboard.writeText(v);
                            toast.success(`Copied ${v}`);
                          }}
                          title="Click to copy"
                        >
                          {v}
                        </code>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="preview" className="mt-4">
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 border-b text-sm text-gray-600">
                    Preview (with sample data)
                  </div>
                  <div 
                    className="p-4 bg-gray-50"
                    dangerouslySetInnerHTML={{ __html: editForm.html_content }}
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-maroon hover:bg-maroon-dark"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Email Preview</DialogTitle>
              <DialogDescription>
                {previewContent?.subject}
              </DialogDescription>
            </DialogHeader>
            
            <div className="border rounded-lg overflow-hidden mt-4">
              <div 
                className="p-4"
                dangerouslySetInnerHTML={{ __html: previewContent?.html_content || '' }}
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminEmailTemplates;
